import { uniqueId } from "@subgraph-io/edge/identification/uniqueId.js";
import { Component } from "@subgraph-io/edge/component/Component.js";
import { objectJoin } from "@subgraph-io/edge/object/objectJoin.js";
import { objectRemoveUndefined } from "@subgraph-io/edge/object/objectRemoveUndefined.js";
/**
 * The DOM class is a thin, optimized utility for implementing common DOM operations on HTML and SVG elements.
 * When used for its static methods, it operates like a functional utility for DOM operations.
 * When used as a constructed instance, it wraps its "root" Element and provides the same DOM manipulation methods as found on the static class, plus the ability to register for delegated events from any number of descendent elements using CSS selectors.
 *
 * To allow composability of application components and DOM elements, DOM implements the DOMRootElement interface and accepts the DOMRootElement interface in all methods that accept Element instances. All a class must do is implement the DOMRootElement interface
 * to opt in to interoperate with all the DOM manipulation methods
 *
 * @example
 * // use DOMRootElement unwrapping support to append one DOM instance to another
 * const dom1 = new DOM('div');
 * const dom2 = new DOM('div');
 * dom1.append(dom2); // implicitly inserts the Element returned by dom2.getDOMRoot() into dom1's root Element
 *
 * // register for delegated event of child element
 * const dom3 = new DOM('div');
 * const registrationId = dom3.registerForEvent('.child-element', 'click', (evt: DOMEvent<MouseEvent>) => console.log(evt.eventData));
 * dom3.unregisterFromEvent(registrationId);
 *
 */
export class DOM {
    /* Constructor */
    /**
     * @param root the Element instance to set as root or the tagName to use when creating the instance root
     * @param settings the Event settings to apply to event handling
     */
    constructor(root, settings = {}) {
        // How Event Registration Works
        // ----------------------------
        // keep a collection of Components, one for each "event type" (ex. click, dblclk, etc.) which is registered in this instance
        // we use Component's custom events ability where the names of the events are CSS selectors
        // naturally, then, for a given Component instance (i.e. event type) you can register N callbacks for any given custom event (i.e. selector) within
        // effectively you can declare things like: "call myCallback1() and myCallback2() whenever a 'click' event occurs on an element matching 'div.clickable' "
        // we register a proxy callback function on the "root Element" to handle triggering callbacks
        // when an event occurs, this proxy function uses the event.type to lookup the corresponding Component and then walks over all its custom events (i.e. selectors) to check if the event.target matches the selector
        // for any selectors that DO match the event.target (i.e. the element on which the event occurred, such as a click), we call any callbacks registered with it.
        // note that because we use Component, we pick up most of the "event bookkeeping" for free, as well as enabling and disabling events
        /* Members */
        this.EVENT_OPTION_DEFAULTS = { bubbles: true, cancelable: true, composed: false };
        this.eventListenerOptions = {};
        this.componentsLookup = {};
        this.eventSpecificationLookup = {};
        this.allDOMEventRegistrationsLookup = {};
        if (typeof root === 'string') {
            const el = this.createHTMLElement(root);
            // if a tagName is passed to createElement that isn't HTML it will resolve as an HTMLUnknownElement.
            // we use this to assume the tag is an SVG tagName and create an SVGElement
            if (el instanceof HTMLUnknownElement) {
                const svgEl = this.createSVGElement(root);
                this.root = svgEl;
            }
            else {
                this.root = el;
            }
        }
        else {
            this.root = root;
        }
        objectJoin(settings, {
            eventTypeOptions: {},
            enableConsoleWarnings: false
        }, 'append');
        this.settings = settings;
        this.proxyCallback = this.proxyCallback.bind(this);
    }
    /**
     * Ensure DOM instance is fully destroyed.  Ensures all events have been unsubscribed from and the DOM's Element has been detached
     */
    destroy() {
        this.unregisterFromEvent();
        this.detach();
    }
    /* Root Handling */
    /**
     * Set the new `root` Element for the DOM instance
     *
     * @param root Element instance to set as the new DOM instance root
     */
    setDOMRoot(root) {
        // unregister our proxyCallback on the existing root and register it on our new event root (for each eventType currently handled)
        for (const eventType in this.eventSpecificationLookup) {
            this.root.removeEventListener(eventType, this.proxyCallback, this.resolveEventListenerOptions(eventType));
            root.addEventListener(eventType, this.proxyCallback, this.resolveEventListenerOptions(eventType));
        }
        this.root = root;
    }
    /**
     * Return the `root` Element of the DOM instance
     */
    getDOMRoot() {
        return this.root;
    }
    /* Node Creating */
    /**
     * Create a new `HTMLElement`
     *
     * @param tagName `HTMLElement` tagName to use for creating new `HTMLElement`
     * @param options `ElementCreationOptions`
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Document/createElement
     */
    static createHTMLElement(tagName, options) {
        return document.createElement(tagName, options);
    }
    /**
     * Create a new `HTMLElement`
     *
     * @param tagName `HTMLElement` tagName to use for creating new `HTMLElement`
     * @param options `ElementCreationOptions`
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Document/createElement
     */
    createHTMLElement(tagName, options) {
        return DOM.createHTMLElement(tagName, options);
    }
    /**
     * Create a new `SVGElement`
     *
     * @param tagName `SVGElement` tagName to use for creating new `SVGElement`
     * @param options `ElementCreationOptions`
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Document/createElementNS
     */
    static createSVGElement(tagName) {
        return document.createElementNS("http://www.w3.org/2000/svg", tagName);
    }
    /**
     * Create a new `SVGElement`
     *
     * @param tagName `SVGElement` tagName to use for creating new `SVGElement`
     * @param options `ElementCreationOptions`
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Document/createElementNS
     */
    createSVGElement(tagName) {
        return DOM.createSVGElement(tagName);
    }
    /* Cursor Handling */
    static setCursor(cursorValue) {
        if (cursorValue !== undefined) {
            document.body.style.cursor = cursorValue;
        }
    }
    /* Node Handling */
    /**
     * Insert element(s) to the end of a parent element's children list
     *
     * @param elements Element(s) or DOMRootElement(s) to append to `parentElement
     * @param parentElement the Element to attach `elements` to.
     *
     * @note if you are appending multiple elements at once, it's recommended that you call append with the array of elements.
     * When you do, the elements will be inserted using a DocumentFragment
     */
    static append(elements, parentElement) {
        const parent = DOM.resolveElement(parentElement);
        // if we have an array of elements, iterate and append them to a DocumentFragment that is then appended to parent
        if (Array.isArray(elements)) {
            const fragment = document.createDocumentFragment();
            const N = elements.length;
            for (let i = 0; i < N; i++) {
                const el = DOM.resolveElement(elements[i]);
                fragment.appendChild(el);
            }
            parent.appendChild(fragment);
        }
        // otherwise elements is scalar; insert it
        else {
            const el = this.resolveElement(elements);
            parent.appendChild(el);
        }
    }
    /**
     * Insert element(s) to the end of a parent element's children list
     *
     * @param elements Element(s) or DOMRootElement(s) to append to `parentElement
     * @param parentElement the Element to attach `elements` to. If omitted append to `root` Element
     *
     * @note if you are appending multiple elements at once, it's recommended that you call append with the array of elements.
     * When you do, the elements will be inserted using a DocumentFragment
     */
    append(elements, parentElement) {
        const parent = parentElement !== undefined ? parentElement : this.getDOMRoot();
        return DOM.append(elements, parent);
    }
    /**
     * Insert element(s) to the beginning of a parent element's children list
     *
     * @param elements Element(s) or DOMRootElement(s) to prepend to parentElement
     * @param parentElement the Element to attach `elements` to.
     *
     * @note if you are prepending multiple elements at once, it's recommended that you call prepend with the array of elements.
     * When you do, the elements will be inserted using a DocumentFragment
     */
    static prepend(elements, parentElement) {
        const parent = DOM.resolveElement(parentElement);
        // if we have an array of elements, iterate and append them
        if (Array.isArray(elements)) {
            const fragment = document.createDocumentFragment();
            const N = elements.length;
            for (let i = 0; i < N; i++) {
                const el = this.resolveElement(elements[i]);
                fragment.appendChild(el);
            }
            parent.insertBefore(fragment, parent.firstChild);
        }
        // otherwise elements is a scalar; prepend it
        else {
            const el = this.resolveElement(elements);
            parent.insertBefore(el, parent.firstChild);
        }
    }
    /**
     * Insert element(s) to the beginning of a parent element's children list
     *
     * @param elements Element(s) or DOMRootElement(s) to prepend to parentElement
     * @param parentElement the Element to attach `elements` to. If omitted prepend to `root` Element
     *
     * @note if you are prepending multiple elements at once, it's recommended that you call prepend with the array of elements.
     * When you do, the elements will be inserted using a DocumentFragment
     */
    prepend(elements, parentElement) {
        const parent = parentElement !== undefined ? parentElement : this.getDOMRoot();
        return DOM.prepend(elements, parent);
    }
    /**
     * Empty element of children
     *
     * @param element the Element to empty.
     * @returns array of removed child Element references
     */
    static empty(element) {
        const el = DOM.resolveElement(element);
        const detachedElements = [];
        let i = 0;
        // iteratively remove element's firstChild until it no longer exists
        while (el.firstChild !== null) {
            detachedElements[i] = el.removeChild(el.firstChild);
            i++;
        }
        return detachedElements;
    }
    /**
     * Empty element of children
     *
     * @param element the Element to empty. If omitted empty `root` Element
     * @returns array of removed child Element references
     */
    empty(element) {
        const el = element !== undefined ? element : this.getDOMRoot();
        return DOM.empty(el);
    }
    /**
     * Replace an element with one or more elements
     *
     * @param elements Element(s) or DOMRootElement(s) to replace `replaceElement` with
     * @param replaceElement the Element to replace.
     *
     */
    static replace(elements, replaceElement) {
        const element = DOM.resolveElement(replaceElement);
        if (replaceElement === elements) {
            return;
        }
        // first, insert the new element or elements just before replaceElement
        DOM.insertBefore(elements, element);
        // remove replaceElement
        DOM.detach(element);
    }
    /**
     * Replace an element with one or more elements
     *
     * @param elements Element(s) or DOMRootElement(s) to replace `replaceElement` with
     * @param replaceElement the Element to replace.
     *
     */
    replace(elements, replaceElement) {
        return DOM.replace(elements, replaceElement);
    }
    /**
     * Detach one or more elements, remaining silent for elements without parents
     *
     * @param elements Element(s) or DOMRootElement(s) to detach
     */
    static detach(elements) {
        // if we have an array of elements, process them
        if (Array.isArray(elements)) {
            // note: parentElement is always defined on elements and is null if the Element has no parent
            let parentElement = null;
            // iterate and detach the elements
            const N = elements.length;
            for (let i = 0; i < N; i++) {
                const el = DOM.resolveElement(elements[i]);
                // we allow an arbitrary array of elements, so we must update parentElement on each iteration
                parentElement = el.parentElement;
                // if the element has a parent element, detach it
                if (parentElement !== null) {
                    parentElement.removeChild(el);
                }
            }
        }
        // otherwise elements is a scalar; detach it
        else {
            const el = DOM.resolveElement(elements);
            // cache the parent element
            const parentNode = el.parentNode;
            // if the element has a parent element, detach it
            if (parentNode !== null) {
                parentNode.removeChild(el);
            }
        }
    }
    /**
     * Detach one or more elements, remaining silent for elements without parents
     *
     * @param elements Element(s) or DOMRootElement(s) to detach
     */
    detach(elements) {
        const els = elements !== undefined ? elements : this.getDOMRoot();
        return DOM.detach(els);
    }
    /**
     * Insert elements before an element
     *
     * @param elements Element(s) or DOMRootElement(s) to insert before `beforeElement`
     * @param beforeElement the Element to insert `elements` before
     *
     * @note if you are inserting multiple elements at once, it's recommended that you call insertBefore with the array of elements.
     * When you do, the elements will be inserted using a DocumentFragment
     */
    static insertBefore(elements, beforeElement) {
        const beforeEl = DOM.resolveElement(beforeElement);
        // cache a reference to parentNode since we use it multiple times in a loop
        const parentNode = beforeEl.parentNode;
        // if parentNode is null (ex. a detached node) don't attempt to call insertBefore() on it
        if (parentNode === null) {
            throw DOM.newError("NoParentNode", "beforeElement");
        }
        // if we have an array of elements, iterate and insert them
        if (Array.isArray(elements)) {
            const fragment = document.createDocumentFragment();
            const N = elements.length;
            for (let i = 0; i < N; i++) {
                const el = DOM.resolveElement(elements[i]);
                fragment.appendChild(el);
            }
            parentNode.insertBefore(fragment, beforeEl);
        }
        // otherwise insert the scalar node
        else {
            const el = DOM.resolveElement(elements);
            parentNode.insertBefore(el, beforeEl);
        }
    }
    /**
     * Insert elements before an element
     *
     * @param elements Element(s) or DOMRootElement(s) to insert before `beforeElement`
     * @param beforeElement the Element to insert `elements` before
     *
     * @note if you are inserting multiple elements at once, it's recommended that you call insertBefore with the array of elements.
     * When you do, the elements will be inserted using a DocumentFragment
     */
    insertBefore(elements, beforeElement) {
        return DOM.insertBefore(elements, beforeElement);
    }
    /**
     * Insert elements after an element
     *
     * @param elements Element(s) or DOMRootElement(s) to insert after `afterElement`
     * @param afterElement the Element to insert `elements` after
     */
    static insertAfter(elements, afterElement) {
        const afterEl = DOM.resolveElement(afterElement);
        // cache a reference to parentNode since we use it multiple times in a loop
        const parentNode = afterEl.parentNode;
        // if parentNode is null (ex. a detached node) don't attempt to call insertBefore() on it
        if (parentNode === null) {
            throw DOM.newError("NoParentNode", "afterElement");
        }
        // if we have an array of elements, iterate and insert them
        if (Array.isArray(elements)) {
            const fragment = document.createDocumentFragment();
            const N = elements.length;
            for (let i = 0; i < N; i++) {
                const el = DOM.resolveElement(elements[i]);
                fragment.appendChild(el);
            }
            if (afterEl.nextSibling === null) {
                parentNode.appendChild(fragment);
            }
            else {
                parentNode.insertBefore(fragment, afterEl.nextSibling);
            }
        }
        // otherwise elements is a scalar; insert it
        else {
            const el = DOM.resolveElement(elements);
            if (afterEl.nextSibling === null) {
                parentNode.appendChild(el);
            }
            else {
                parentNode.insertBefore(el, afterEl.nextSibling);
            }
        }
    }
    /**
     * Insert elements after an element
     *
     * @param elements Element(s) or DOMRootElement(s) to insert after `afterElement`
     * @param afterElement the Element to insert `elements` after
     */
    insertAfter(elements, afterElement) {
        return DOM.insertAfter(elements, afterElement);
    }
    /* Text Node Handling */
    /**
     * Append text node to element
     *
     * @param text text to insert into text node
     * @param parentElement the Element to append text node to
     *
     * @returns Text node created
     */
    static appendText(text, parentElement) {
        const parent = DOM.resolveElement(parentElement);
        const textNode = document.createTextNode(text);
        parent.appendChild(textNode);
        return textNode;
    }
    /**
     * Append text node to element
     *
     * @param text text to insert into text node
     * @param parentElement the Element to append text node to
     *
     * @returns Text node created
     */
    appendText(text, parentElement) {
        const el = parentElement !== undefined ? parentElement : this.getDOMRoot();
        return DOM.appendText(text, el);
    }
    /**
     * Prepend text node to element
     *
     * @param text text to insert into text node
     * @param parentElement the Element to prepend text node to
     *
     * @returns Text node created
     */
    static prependText(text, parentElement) {
        const parent = this.resolveElement(parentElement);
        const textNode = document.createTextNode(text);
        if (parent.firstChild === null) {
            parent.appendChild(textNode);
        }
        else {
            parent.insertBefore(textNode, parent.firstChild);
        }
        return textNode;
    }
    /**
     * Prepend text node to element
     *
     * @param text text to insert into text node
     * @param parentElement the Element to prepend text node to
     *
     * @returns Text node created
     */
    prependText(text, parentElement) {
        const el = parentElement !== undefined ? parentElement : this.getDOMRoot();
        return DOM.prependText(text, el);
    }
    /**
     * Generate a unique CSS id selector for element and apply it.  If element already has an id, leaves as is.
     *
     * @param element Element or DOMRootElement to generate CSS id for
     * @param id the optional id to use.  If not passed unique id is generated
     *
     * @returns CSS id selector (ex. #EDGE-DOM-12345)
     */
    static setUniqueId(element, prefix = '', returnSelector = false) {
        const el = DOM.resolveElement(element);
        const id = uniqueId(prefix);
        DOM.setAttribute('id', id, el);
        return (returnSelector === true) ? `#${id}` : id;
    }
    /**
     * Generate a unique CSS id selector for element and apply it.  If element already has an id, leaves as is.
     *
     * @param element Element or DOMRootElement to generate CSS id for
     * @param id the optional id to use.  If not passed unique id is generated
     *
     * @returns CSS id selector (ex. #EDGE-DOM-12345)
     */
    setUniqueId(element, prefix = '', returnSelector = false) {
        return DOM.setUniqueId(element, prefix, returnSelector);
    }
    /* Attribute and Style Handling */
    /**
     * Set attribute on element
     *
     * @param name name of attribute to set
     * @param value value to set for attribute
     * @param element Element or DOMRootElement to set attribute on.
     */
    static setAttribute(name, value, element) {
        const el = DOM.resolveElement(element);
        if ((el instanceof HTMLElement) === false) {
            el.setAttributeNS(null, name, value);
        }
        else {
            el.setAttribute(name, value);
        }
    }
    /**
     * Set attribute on element
     *
     * @param name name of attribute to set
     * @param value value to set for attribute
     * @param element Element or DOMRootElement to set attribute on. If omitted, set on `root` Element
     */
    setAttribute(name, value, element) {
        const el = element !== undefined ? element : this.getDOMRoot();
        return DOM.setAttribute(name, value, el);
    }
    /**
     * Set attributes on element
     *
     * @param attributes object where keys are names of attributes and values are the attribute values
     * @param element Element or DOMRootElement to set attribute on.
     */
    static setAttributes(attributes, element) {
        for (const name in attributes) {
            DOM.setAttribute(name, attributes[name], element);
        }
    }
    /**
     * Set attributes on element
     *
     * @param attributes object where keys are names of attributes and values are the attribute values
     * @param element Element or DOMRootElement to set attribute on.
     */
    setAttributes(attributes, element) {
        const el = element !== undefined ? element : this.getDOMRoot();
        return DOM.setAttributes(attributes, el);
    }
    /**
     * Remove an attribute from the element
     *
     * @param name name of attribute to remove
     * @param element Element or DOMRootElement to remove the attribute from.
     */
    static removeAttribute(name, element) {
        const el = DOM.resolveElement(element);
        if ((el instanceof HTMLElement) === false) {
            el.removeAttributeNS(null, name);
        }
        else {
            el.removeAttribute(name);
        }
    }
    /**
     * Remove an attribute from the element
     *
     * @param name name of attribute to remove
     * @param element Element or DOMRootElement to remove the attribute from.
     */
    removeAttribute(name, element) {
        DOM.removeAttribute(name, element);
    }
    /**
     * Removes attributes from an element
     *
     * @param attributes an array of attributes to be removed
     * @param element Element or DOMRootElement to remove the attribute from.
     */
    static removeAttributes(attributes, element) {
        const N = attributes.length;
        for (let i = 0; i < N; i++) {
            DOM.removeAttribute(attributes[i], element);
        }
    }
    /**
     * Removes attributes from an element
     *
     * @param attributes an array of attributes to be removed
     * @param element Element or DOMRootElement to remove the attribute from.
     */
    removeAttributes(attributes, element) {
        DOM.removeAttributes(attributes, element);
    }
    /**
     * Get attribute on element
     *
     * @param name name of attribute to get
     * @param element Element or DOMRootElement to get attribute from. If omitted, get attribute from `root` Element
     */
    static getAttribute(name, element) {
        const el = DOM.resolveElement(element);
        if ((el instanceof HTMLElement) === false) {
            return el.getAttributeNS(null, name);
        }
        return el.getAttribute(name);
    }
    /**
     * Get attribute on element
     *
     * @param name name of attribute to get
     * @param element Element or DOMRootElement to get attribute from. If omitted, get attribute from `root` Element
     */
    getAttribute(name, element) {
        const el = element !== undefined ? element : this.getDOMRoot();
        return DOM.getAttribute(name, el);
    }
    /**
     * Set one or more styles on an element
     *
     * @param styles object of css declarations to add as inline styles on Element
     * @param element HTML/SVGElement or DOMRootElement wrapping an HTML/SVGElement to set styles on.
     */
    static setStyles(styles, element) {
        const el = DOM.resolveElement(element);
        for (const styleName in styles) {
            // note: we need the "as CSSProperty" here because TS thinks the for-in might traverse a non-owned property which might not be one of the CSSProperty strings
            if (styles[styleName] !== undefined) {
                el.style.setProperty(styleName, styles[styleName]);
            }
        }
    }
    /**
     * Set one or more styles on an element
     *
     * @param styles object of css declarations to add as inline styles on Element
     * @param element HTML/SVGElement or DOMRootElement wrapping an HTML/SVGElement to set styles on. If omitted, styles set on `root` Element
     */
    setStyles(styles, element) {
        const el = element !== undefined ? element : this.getDOMRoot();
        return DOM.setStyles(styles, el);
    }
    /**
     * Remove one or more styles on an element
     *
     * @param styles array of css declaration keys to remove from inline styles on Element. If omitted, remove all styles on Element
     * @param element HTML/SVGElement or DOMRootElement wrapping an HTML/SVGElement to remove styles from. If omitted, styles removed from `root` Element
     */
    static removeStyles(styles, element) {
        const el = this.resolveElement(element);
        if (styles === undefined) {
            return this.setAttribute('style', '', el);
        }
        if (Array.isArray(styles)) {
            const N = styles.length;
            for (let i = 0; i < N; i++) {
                el.style.removeProperty(styles[i]);
            }
        }
        else {
            el.style.removeProperty(styles);
        }
    }
    /**
     * Remove one or more styles on an element
     *
     * @param styles array of css declaration keys to remove from inline styles on Element. If omitted, remove all styles on Element
     * @param element HTML/SVGElement or DOMRootElement wrapping an HTML/SVGElement to remove styles from. If omitted, styles removed from `root` Element
     */
    removeStyles(styles, element) {
        const el = element !== undefined ? element : this.getDOMRoot();
        return DOM.removeStyles(styles, el);
    }
    registerForEvent(selector, eventSpecification, callback, scope, holdData) {
        // resolve the eventType and keyModifiers
        const { eventType, modifierKeySet } = this.resolveEventSpecification(eventSpecification);
        // create the event manager if necessary
        let componentId = this.findComponent(eventType, modifierKeySet);
        if (componentId === undefined) {
            // if the eventType isn't already registered on our root, do so since we're about to register some activity on that eventType
            if (this.eventSpecificationLookup[eventType] === undefined) {
                this.root.addEventListener(eventType, this.proxyCallback, this.resolveEventListenerOptions(eventType));
            }
            // create the event manager and update the eventManagerLookup and eventSpecificationLookup
            componentId = uniqueId();
            this.componentsLookup[componentId] = new Component();
            if (this.eventSpecificationLookup.hasOwnProperty(eventType) === false) {
                this.eventSpecificationLookup[eventType] = {};
            }
            this.eventSpecificationLookup[eventType][componentId] = modifierKeySet;
        }
        // register our callback and capture the corresponding registration id for return
        const registrationId = this.componentsLookup[componentId].registerForEvent('CLASS_EVENT', selector, callback, scope, holdData);
        // update the all registrations lookup
        this.allDOMEventRegistrationsLookup[registrationId] = { selector: selector, eventType: eventType, componentId: componentId };
        // return the registration id
        return registrationId;
    }
    /**
     * Unregister DOM events
     *
     * @param registrationId
     * @param selector
     * @param eventType
     */
    unregisterFromEvent(registrationId, selector, eventSpecification, garbageCollectEvents = true) {
        // initialize the return object
        let unregistrations = {};
        // received a registrationId
        if (registrationId !== undefined) {
            // if the registration does not exist in our lookup, throw an error
            if (this.eventRegistrationExists(registrationId) === false) {
                throw DOM.newError('RegistrationIdNotFound', registrationId);
            }
            // otherwise, proceed with removing it
            else {
                // fetch the corresponding componentId
                const { eventType, componentId } = this.allDOMEventRegistrationsLookup[registrationId];
                // unregister the particular registrationId
                this.componentsLookup[componentId].unregisterFromEvent(registrationId);
                // capture the unregistration
                unregistrations[registrationId] = this.allDOMEventRegistrationsLookup[registrationId];
                // remove the corresponding data from our info lookup
                delete this.allDOMEventRegistrationsLookup[registrationId];
                // if enabled, clean up empty lookups and unused root listeners for the eventType of interest
                if (garbageCollectEvents) {
                    this.garbageCollectEvents(eventType);
                }
            }
        }
        // no registrationId, but received a selector and eventSpecification
        else if (selector !== undefined && eventSpecification !== undefined) {
            // resolve the eventType and keyModifiers and attempt to find the event manager
            const { eventType, modifierKeySet } = this.resolveEventSpecification(eventSpecification);
            const componentId = this.findComponent(eventType, modifierKeySet);
            // make sure we have even have this kind of event
            if (componentId !== undefined) {
                if (this.componentsLookup[componentId].eventExists('CLASS_EVENT', selector)) {
                    this.componentsLookup[componentId].destroyEvent('CLASS_EVENT', selector);
                    // regsistration ids from this.allDOMEventRegistrationsLookup which correspond to this selector and event manager (unique collection of event and modifier keys)
                    for (const registrationId in this.allDOMEventRegistrationsLookup) {
                        const registrationEntry = this.allDOMEventRegistrationsLookup[registrationId];
                        if (registrationEntry.selector === selector && registrationEntry.componentId === componentId) {
                            // if the modifier keys match, then we're indeed removing this registration
                            unregistrations[registrationId] = registrationEntry;
                            // remove the corresponding data from our info lookup
                            delete this.allDOMEventRegistrationsLookup[registrationId];
                        }
                    }
                }
            }
            // if enabled, clean up empty lookups and unused root listeners for the eventType of interest
            if (garbageCollectEvents) {
                this.garbageCollectEvents(eventType);
            }
        }
        // no registrationId, no eventSpecification but received a selector
        else if (selector !== undefined) {
            // check all event managers for the selector and destroy the corresponding custom event if it exists
            for (const componentId in this.componentsLookup) {
                if (this.componentsLookup[componentId].eventExists('CLASS_EVENT', selector)) {
                    // destroy the custom event associated with the event specification
                    this.componentsLookup[componentId].destroyEvent('CLASS_EVENT', selector);
                }
            }
            // remove all regsistration ids from this.allDOMEventRegistrationsLookup which correspond to this selector
            for (const registrationId in this.allDOMEventRegistrationsLookup) {
                if (this.allDOMEventRegistrationsLookup[registrationId].selector === selector) {
                    // capture the unregistration
                    unregistrations[registrationId] = this.allDOMEventRegistrationsLookup[registrationId];
                    // remove the corresponding data from our info lookup
                    delete this.allDOMEventRegistrationsLookup[registrationId];
                }
            }
            // if enabled, clean up empty lookups and unused root listeners (do this across eventTypes because only a selector was specified)
            if (garbageCollectEvents) {
                this.garbageCollectEvents();
            }
        }
        // no registrationId, no selector but received eventSpecification
        else if (eventSpecification !== undefined) {
            // resolve the eventType and keyModifiers and attempt to find the event manager
            const { eventType, modifierKeySet } = this.resolveEventSpecification(eventSpecification);
            const componentId = this.findComponent(eventType, modifierKeySet);
            // make sure we even have this event manager
            if (componentId !== undefined) {
                // empty the event manager for this event specification (well, we only use the CLASS_EVENT registry within, so empty THAT registry)
                this.componentsLookup[componentId].clearRegistry('CLASS_EVENT');
                // remove all registration ids from this.allDOMEventRegistrationsLookup which correspond to this event specification
                for (const registrationId in this.allDOMEventRegistrationsLookup) {
                    if (this.allDOMEventRegistrationsLookup[registrationId].componentId === componentId) {
                        // capture the unregistration
                        unregistrations[registrationId] = this.allDOMEventRegistrationsLookup[registrationId];
                        // remove the corresponding data from our info lookup
                        delete this.allDOMEventRegistrationsLookup[registrationId];
                    }
                }
                // if enabled, clean up empty lookups and unused root listeners for the eventType of interest
                if (garbageCollectEvents) {
                    this.garbageCollectEvents(eventType);
                }
            }
        }
        // no registrationId, no eventSpecification, no selector
        else {
            // iterate the event managers for this eventType
            for (const componentId in this.componentsLookup) {
                // empty the event manager for this eventType (well, we only use the CLASS_EVENT registry within, so empty THAT registry)
                this.componentsLookup[componentId].clearRegistry('CLASS_EVENT');
            }
            // capture the unregistrations
            unregistrations = this.allDOMEventRegistrationsLookup;
            // remove all registration ids from this.allDOMEventRegistrationsLookup (instead of iterating and deleting them all from the object, just reassign to an empty object for performance)
            this.allDOMEventRegistrationsLookup = {};
            // if enabled, clean up empty lookups and unused root listeners
            if (garbageCollectEvents) {
                this.garbageCollectEvents();
            }
        }
        // if we're throwing warnings, check if this unregister essentially did nothing
        if (this.settings.enableConsoleWarnings === true && Object.keys(unregistrations).length === 0) {
            let eventSpecificationString = eventSpecification;
            if (typeof eventSpecification !== undefined && typeof eventSpecification !== 'string') {
                eventSpecificationString = JSON.stringify(eventSpecification);
            }
            console.warn(`A call to unregisterFromEvents() with selector "${selector}" and eventSpecification "${eventSpecificationString}" resulted in no removed registrations.`);
        }
        return unregistrations;
    }
    /**
     * Trigger event on root element or specified `target`
     *
     * @param target
     * @param eventType
     * @param eventConstructor
     * @param eventOptions
     */
    triggerEvent(target, eventType, eventConstructor = Event, eventOptions = {}) {
        objectJoin(eventOptions, this.EVENT_OPTION_DEFAULTS, 'append');
        return target.dispatchEvent(new eventConstructor(eventType, eventOptions));
    }
    // checks if an event registrationId exists
    eventRegistrationExists(registrationId) {
        // return true if the registration id exists in the "all events" lookup
        return this.allDOMEventRegistrationsLookup.hasOwnProperty(registrationId);
    }
    // enables an event by registration id (probably easier for the user to cache this id vs having to pass gory details such as event type, modifier key set, and selector)
    enableEvent(registrationId) {
        const registrationInfo = this.allDOMEventRegistrationsLookup[registrationId];
        return this.componentsLookup[registrationInfo.componentId].enableEvent('CLASS_EVENT', registrationInfo.selector);
    }
    // disables an event by registration id (probably easier for the user to cache this id vs having to pass gory details such as event type, modifier key set, and selector)
    disableEvent(registrationId) {
        const registrationInfo = this.allDOMEventRegistrationsLookup[registrationId];
        this.componentsLookup[registrationInfo.componentId].disableEvent('CLASS_EVENT', registrationInfo.selector);
    }
    /* Settings Management */
    setSettings(settings) {
        objectJoin(this.settings, settings, 'merge');
    }
    getSettings() {
        return this.settings;
    }
    setSetting(setting, value) {
        this.settings[setting] = value;
    }
    getSetting(setting) {
        return this.settings[setting];
    }
    setEventListenerOptions(eventType, eventListenerOptions) {
        this.eventListenerOptions[eventType] = eventListenerOptions;
    }
    // get the "options" used for registering and unregistering events for the specified event type; return defaults if never explicitly set
    resolveEventListenerOptions(eventType) {
        return this.eventListenerOptions[eventType];
    }
    getEventModifierKeys(event) {
        // these will be undefined for non keyboard and mouse events, which is totally fine by design (undefined means "don't care, anything goes")
        return {
            Alt: event.altKey,
            Control: event.ctrlKey,
            Shift: event.shiftKey,
            Meta: event.metaKey
        };
    }
    /* Helper */
    // resolves the element
    static resolveElement(element) {
        if ((element instanceof Element) === false) {
            // in a TS project element must be Element or DOMRootElement and this runtime condition checking is superfluous
            // in a JS project who knows, so throw a meaningful error if element or value returned from element.getDOMRoot() is not an Element
            if (element !== undefined && element.getDOMRoot !== undefined) {
                const el = element.getDOMRoot();
                if ((el instanceof Element) === false) {
                    throw DOM.newError("ElementIsInvalid", 'DOMRootElement', element, el);
                }
                return el;
            }
            throw DOM.newError("ElementIsInvalid", element);
        }
        return element;
    }
    resolveElement(element) {
        return DOM.resolveElement(element);
    }
    // throws the specified error
    static newError(errorName, ...args) {
        switch (errorName) {
            case "NoParentNode":
                return new Error(`Error: NoParentNode. The provided node '${args[0]}' did not have a parentNode (may be a fragment or detached element).`);
            case "EventTargetWasNull":
                return new Error(`Error: EventTargetWasNull. The event.target was null somehow. Here's the event type detected: ${args[0]}.`);
            case "RegistrationIdNotFound":
                return new Error(`Error: RegistrationIdNotFound.  The following registrationId was not found: "${args[0]}".`);
            case "ElementIsInvalid":
                if (args[0] === 'DOMRootElement') {
                    return new Error(`Error: ElementIsInvalid. The DOMRootElement passed does not return valid Element from DOMRootElement.getDOMRoot(). DOMRootElement: ${args[2]}. DOMRootElement.getDOMRoot(): ${args[1]}.`);
                }
                return new Error(`Error: ElementIsInvalid. The element passed is invalid. Element: ${args[0]}.`);
        }
    }
    // resolve the eventSpecification which can be either a string or an object
    resolveEventSpecification(eventSpecification) {
        // resolve the eventType and modifierKeySet
        let eventType = eventSpecification;
        let modifierKeySet = {};
        if (typeof eventSpecification !== 'string') {
            eventType = eventSpecification.eventType;
            modifierKeySet = eventSpecification.modifierKeySet;
        }
        return { eventType: eventType, modifierKeySet: modifierKeySet };
    }
    // returns the event manager and it's id if it exists, otherwise returns undefined
    findComponent(eventType, modifierKeySet) {
        // check if we even have this eventType
        if (this.eventSpecificationLookup.hasOwnProperty(eventType)) {
            // if we get here, iterate to search for the event manager
            const lookupEntry = this.eventSpecificationLookup[eventType];
            for (const componentId in lookupEntry) {
                const comparisonModifierKeys = lookupEntry[componentId];
                if (this.modifierKeySetsFunctionallyEquivalent(modifierKeySet, comparisonModifierKeys)) {
                    return componentId;
                }
            }
        }
        return undefined;
    }
    // check whether specified "expected" keys are satistifed by specified "comparison" keys (note that undefined for a given modifier key is a "don't care" condition so it matches either way)
    modifierKeySetSatisfied(expected, comparison) {
        // iterate expected keys, checking values against incoming. if any fail, exit and return false.  note: it's ok if incoming has more because any keys NOT in expected are considered "ok either way"
        for (const key in expected) {
            // TS complains that key is a general string vs knowing it must be one of the keys in a ModifierKeySet (seems like a forin loop ts check fail)
            if (expected[key] !== comparison[key]) {
                return false;
            }
        }
        return true;
    }
    // checks if two modifierSets are functionally equivalent by making sure all non-undefined elements are equivalent
    modifierKeySetsFunctionallyEquivalent(modifierKeySetA, modifierKeySetB) {
        // remove undefined elements both sets
        objectRemoveUndefined(modifierKeySetA);
        objectRemoveUndefined(modifierKeySetB);
        // if the sets do not have the same number of keys now, return false
        if (Object.keys(modifierKeySetA).length !== Object.keys(modifierKeySetB).length) {
            return false;
        }
        // if the sets do not have the same key names or do not have equal values, return false
        for (const modifierKeyName in modifierKeySetA) {
            if (modifierKeySetB.hasOwnProperty(modifierKeyName) === false || modifierKeySetB[modifierKeyName] !== modifierKeySetA[modifierKeyName]) {
                return false;
            }
        }
        return true;
    }
    // clean up root event listeners if nothing can currently fire on it; return true if garbage collected something, false otherwise
    garbageCollectEvents(eventType) {
        // resolve the event types to process
        let eventTypes = Object.keys(this.eventSpecificationLookup);
        if (eventType !== undefined) {
            // prepare the "clean up single, specified event type" case
            if (eventTypes.includes(eventType)) {
                eventTypes = [eventType];
            }
            else {
                // there's nothing to garbage collect if this eventType doesn't even exist
                return false;
            }
        }
        let collected = false;
        // remove event specification lookup entries if they no longer have any eventTypes and remove the root listeners for that eventType
        const N = eventTypes.length;
        for (let i = 0; i < N; i++) {
            const eventType = eventTypes[i];
            // clean up empty event managers for this eventType
            for (const componentId in this.eventSpecificationLookup[eventType]) {
                if (this.componentsLookup[componentId].registryHasEvents('CLASS_EVENT') === false) {
                    delete this.componentsLookup[componentId];
                    delete this.eventSpecificationLookup[eventType][componentId];
                    collected = true;
                }
            }
            // clean up the eventType if unused (and remove the corresponding root listener)
            if (Object.keys(this.eventSpecificationLookup[eventType]).length === 0) {
                delete this.eventSpecificationLookup[eventType];
                this.root.removeEventListener(eventType, this.proxyCallback, this.resolveEventListenerOptions(eventType));
                collected = true;
            }
        }
        return collected;
    }
    // looks up the relevant Component by event.type and calls any callbacks for selector "custom events" which match the event.target
    proxyCallback(event) {
        const eventTarget = event.target;
        const eventType = event.type;
        // throw an error if target is null
        if (eventTarget === null) {
            throw DOM.newError("EventTargetWasNull", eventType);
        }
        // iterate the event managers for this event type (one for each functionally unique modifier key set)
        for (const componentId in this.eventSpecificationLookup[eventType]) {
            const modifierKeySet = this.eventSpecificationLookup[eventType][componentId];
            // if the event satisfies this event manager's modifier key set, proceed to trigger for any matching selectors
            if (this.modifierKeySetSatisfied(modifierKeySet, this.getEventModifierKeys(event))) {
                const component = this.componentsLookup[componentId];
                const selectors = component.getRegistryEventNames('CLASS_EVENT');
                const N = selectors.length;
                for (let i = 0; i < N; i++) {
                    const selector = selectors[i];
                    const assumedElement = eventTarget;
                    // trigger for matching selectors
                    if (selector === '*' || assumedElement.matches(selector) || assumedElement.closest(selector) !== null) {
                        component.triggerEvent('CLASS_EVENT', selector, event);
                    }
                }
            }
        }
    }
}
//# sourceMappingURL=DOM.js.map